/**
 * TrendCompare · Module A 投放趋势对比
 *
 * T10 产出，需求 §4.4.4
 *
 * 结构：
 * - CardCommon 外壳（title 模块名 + subFilterBeforeElement 指标 Segment）
 * - operations：图表类型切换（line / bar / pie / table）
 * - hasDownload：右侧下载按钮（暂占位，实际下载在 T13 接入）
 * - 主体：根据图表类型渲染折线图 / 柱状图 / 饼图 / 数据表
 *
 * 设计参考 figma：
 * - 折线图：3251-41860
 * - 柱状图：3270-41219
 *
 * 性能：
 * - 文案预先固化为字符串
 * - gameMap 由父页面统一从 cond 拉取后下发
 */
import React, { useMemo, useState } from 'react';
import { Segment } from 'tea-component';
import CardCommon, { operation } from '@op/components/CardCommon';
import { lineIcon, barIcon, pieIcon, tableIcon } from '@op/constants/chart';
import useIntls from '@opm/hooks/useIntls';
import { IFilter } from '../../types/filter';
import { TMetric } from '../../types/common';
import {
  METRIC_METAS,
  TREND_METRICS,
  DEFAULT_TREND_METRIC,
  getMetricLabel,
} from '../../constants/metrics';
import { DBO_TM } from '@op/utils/format';
import { gotoDownload } from '@opm/pages/Download';
import { useTrendCompare } from '../../hooks/useTrendCompare';
import { useGameColorContext } from '../../context/GameColorContext';
import TrendLineChart from './TrendLineChart';
import TrendBarChart from './TrendBarChart';
import TrendPieChart from './TrendPieChart';
import TrendDataTable from './TrendDataTable';
import './index.scss';
import BubbleCommon from '@op/components/BubbleCommon';
import { getDayMode } from '@op/pages/adCreatives/overview/utils/metricLabels';

export const TREND_COMPARISON_TITLE = 'Trend Comparison';

export interface ITrendCompareProps {
  filter: IFilter;
  id?: string;
  className?: string;
  emptyTitleText: string;
}

/** 趋势图本模块支持的图表类型 */
type TTrendChartType = 'line' | 'bar' | 'pie' | 'table';

const MIN_HEIGHT = 360;

export default function TrendCompare({
  filter,
  id,
  className,
  emptyTitleText,
}: ITrendCompareProps) {
  const { intl } = useIntls();
  const { gameMap } = useGameColorContext();
  const [metric, setMetric] = useState<TMetric>(DEFAULT_TREND_METRIC);
  const [chartType, setChartType] = useState<TTrendChartType>('line');

  const { series, loading } = useTrendCompare({ filter, metric });

  /** 文案预先固化为字符串 */
  const labels = useMemo(
    () => ({
      title: intl(TREND_COMPARISON_TITLE),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [intl],
  );

  /** 指标 Segment 选项 */
  const metricOptions = useMemo(
    () =>
      TREND_METRICS.map((m) => ({
        value: m,
        text: (
          <>
            {METRIC_METAS[m]?.tipsMap ? (
              <BubbleCommon
                tipsMap={METRIC_METAS[m]?.tipsMap || undefined}
                selected={metric === m}
              >
                {intl(getMetricLabel(METRIC_METAS[m], filter.date_type))}
              </BubbleCommon>
            ) : (
              intl(getMetricLabel(METRIC_METAS[m], filter.date_type))
            )}
          </>
        ),
      })),
    [intl, metric, filter.date_type],
  );

  /** 当前指标的国际化 label（pie tooltip 行左侧文案） */
  const { metricLabel, metricPieLabel } = useMemo(() => {
    const currentMeta = METRIC_METAS[metric];
    const labelStr = getMetricLabel(currentMeta, filter.date_type);
    const dayMode = getDayMode(
      filter.date_type,
      filter.start_time,
      filter.end_time,
    );

    let pieLabelStr: string;
    const rawPieLabel = currentMeta?.pieLabel;
    if (rawPieLabel) {
      pieLabelStr =
        typeof rawPieLabel === 'object'
          ? rawPieLabel[dayMode === 'single' ? 'single' : 'multi']
          : rawPieLabel;
    } else {
      pieLabelStr = labelStr;
    }

    return {
      metricLabel: intl(labelStr),
      metricPieLabel: intl(pieLabelStr),
    };
  }, [intl, metric, filter.date_type, filter.start_time, filter.end_time]);

  /** subFilterBeforeElement 区域：指标 Segment */
  const subFilterBefore = (
    <Segment
      className="dbom-trend-compare__metric"
      value={metric}
      options={metricOptions}
      onChange={(value) => setMetric(value as TMetric)}
    />
  );

  /** 根据 chartType 渲染对应视图 */
  const renderChart = () => {
    switch (chartType) {
      case 'bar':
        return (
          <TrendBarChart
            series={series}
            gameMap={gameMap}
            dateType={filter.date_type}
            height={MIN_HEIGHT}
            emptyTitleText={emptyTitleText}
          />
        );
      case 'pie':
        // 仅 chartType=pie 时挂载；TrendPieChart 内部自带 useTrendPieSummary（dimension_summary）
        return (
          <TrendPieChart
            filter={filter}
            metric={metric}
            gameMap={gameMap}
            metricLabel={metricPieLabel}
            height={MIN_HEIGHT}
            emptyTitleText={emptyTitleText}
          />
        );
      case 'table':
        return (
          <TrendDataTable
            series={series}
            gameMap={gameMap}
            filter={filter}
            emptyTitleText={emptyTitleText}
            height={MIN_HEIGHT - 49.5}
          />
        );
      case 'line':
      default:
        return (
          <TrendLineChart
            series={series}
            gameMap={gameMap}
            dateType={filter.date_type}
            height={MIN_HEIGHT}
            emptyTitleText={emptyTitleText}
          />
        );
    }
  };

  return (
    <CardCommon
      id={id}
      className={`dbom-trend-compare-card ${className ?? ''}`}
      title={labels.title}
      subFilterBeforeElement={subFilterBefore}
      loading={loading}
      loadingHeight={MIN_HEIGHT}
      emptyHeight={MIN_HEIGHT}
      operations={[lineIcon, barIcon, pieIcon, tableIcon]}
      defaultOperation={chartType}
      handleOperationsOnClick={(item: operation) => {
        setChartType((item?.symbol as TTrendChartType) || 'line');
      }}
      hasDownload
      handleDownloadOnClick={() => {
        void gotoDownload({
          key: 'MarketingCompetitorAdComparisonTrend',
          fileName: `Competitor_Ad_Comparison_Trend_${metricLabel.replace(
            /\s+/g,
            '_',
          )}_${DBO_TM.timeTitle({
            start_time: filter.start_time,
            end_time: filter.end_time,
            date_type: filter.date_type,
          })}`,
          filter,
          // 直接传 hook 输出的友好结构；config.dealWithDataFunc 会扁平化
          data: series,
          auxiliaryParams: { gameMap, metricLabel },
        });
      }}
    >
      {renderChart()}
    </CardCommon>
  );
}
